import React, { useEffect } from 'react';
import { Image } from 'expo-image';
import { VStack, Box, Pressable, Skeleton } from 'native-base';

// Classes & Types
import { User, Flair } from '@breakroom-nx/classes';
import { AccountType, FlairType } from '@breakroom-nx/helpers';
import { getAuth } from 'firebase/auth';

import { useDispatch } from 'react-redux';
import {
  useAppSelector,
  selectUser,
  getProfilePanelState,
  getPublicUserProfilePanelState,
  togglePublicUserProfilePanel,
  useLazyGetUserFromUIDQuery,
} from '@breakroom-nx/redux';

export interface UserAvatarProps {
  uid: string;
  user?: User;
  flair?: Flair | null;
  squeeze?: boolean;
  size?: number;
  zIndex?: number;
  borderColor?: string;
  disabled?: boolean;
  noMargin?: boolean;
  noBorder?: boolean;
  center?: boolean; // Optically centers component (see (1) below)
  avatarURL?: string;
  theme?: string;
}

export function UserAvatar({
  uid,
  user = undefined,
  squeeze = false,
  flair = undefined,
  size = 100,
  zIndex = 1,
  borderColor = 'primary.400',
  disabled = false,
  noMargin = false,
  noBorder = false,
  center = false,
  avatarURL = undefined,
  theme = undefined,
}: UserAvatarProps) {
  // const flair: Flair = props.flair;
  const dispatch = useDispatch();
  const auth = getAuth();
  const sizeInPixels = typeof size === 'string' ? parseFloat(size) : size;
  //    ^^^^^^^^^^^^
  // For borderWidth and flair position calculation
  const flairBottomOffset =
    sizeInPixels < 90 ? parseFloat('-11px') : -sizeInPixels / 4.5;

  // Panel Controls
  const otherUserProfileDisplayed = useAppSelector(
    getPublicUserProfilePanelState
  );

  //! APIs
  const [trigger, { data: fetchedUser, isLoading }] =
    useLazyGetUserFromUIDQuery();

  const onPress = () => {
    dispatch(selectUser(uid));
    if (!otherUserProfileDisplayed) {
      dispatch(togglePublicUserProfilePanel());
    }
  };

  useEffect(() => {
    if (!avatarURL && !user) {
      // console.log(`No user provided, fetching avatar url...`);
      trigger(uid);
    } else {
      // console.log(`Avatar or user passed in!`);
    }
  }, []);

  return (
    <Pressable
      mt={flair && center ? -flairBottomOffset : 0}
      //           ^^^^^^
      // (1) Keeps UserAvatar centered in H/VStacks, compensating for flair.
      onPress={disabled || uid === auth.currentUser?.uid ? null : onPress}
      pointerEvents={
        disabled || uid === auth.currentUser?.uid ? 'none' : 'auto'
      }
    >
      <VStack
        alignItems={'center'}
        ml={noMargin ? 0 : squeeze ? '-17px' : '3px'}
        zIndex={zIndex}
      >
        {isLoading ? (
          <Skeleton endColor="surface.400" size={size} rounded="full" />
        ) : (
          <Box
            rounded={'full'}
            size={size}
            borderWidth={noBorder ? 0 : sizeInPixels < 90 ? '1.3px' : 2.5}
            borderColor={flair ? flair.getColor() : borderColor}
            backgroundColor={
              theme ? theme : user ? user.theme : fetchedUser?.theme
            }
            overflow={'hidden'}
          >
            <Image
              source={{
                uri: avatarURL
                  ? avatarURL
                  : user
                  ? user.avatar
                  : fetchedUser?.avatar,
              }}
              cachePolicy={'memory'}
              accessibilityLabel={`${
                user ? user.username : fetchedUser?.username
              }'s avatar`}
              key={user ? user.avatar : fetchedUser?.avatar}
              contentFit={'cover'}
              style={{
                height: '100%',
                aspectRatio: 1,
              }}
            />
          </Box>
        )}

        {flair ? (
          <Box zIndex={10} position={'absolute'} bottom={flairBottomOffset}>
            {flair.getIcon(
              sizeInPixels < 90 ? (sizeInPixels < 50 ? 17 : 23) : 30,
              borderColor
            )}
          </Box>
        ) : null}
      </VStack>
    </Pressable>
  );
}
